/**
 * Runtime guards used by optimistic/in-place cache mutations.
 *
 * Mirrors `src/lib/cache/questions/helpers.ts`. Kept in a leaf module (no barrel import) so
 * `operations.ts` can depend on it directly without going through `./index`, which would
 * otherwise create an import cycle (helpers -> barrel -> operations -> helpers).
 *
 * Unlike questions (which has three cache-item variants -- QuestionRead / QuestionDetailRead /
 * QuestionProfileRead -- and so needs a `QuestionCacheItem` union type), every event list and
 * detail cache in this codebase stores the same `EventRead` shape, so no separate `types.ts` /
 * union alias is needed here.
 *
 * `isRecord` itself is defined once in `../collections` and re-exported here (and from
 * `questions/helpers.ts`) so both domains -- and `../collections`'s own `patchDetailData` --
 * share one implementation (#953 review).
 */

import type { EventRead } from '@models/api/event';
import { isRecord } from '../collections';

export { isRecord } from '../collections';

/**
 * Duck-types an `EventRead` out of an unknown cache value. Checks a handful of required,
 * cheap-to-verify fields (mirrors `isQuestionCacheItem`'s selection strategy) rather than every
 * field on the interface.
 */
export const isEventCacheItem = (value: unknown): value is EventRead => {
  if (!isRecord(value)) return false;

  return (
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    typeof value.slug === 'string' &&
    typeof value.status === 'string' &&
    typeof value.participant_count === 'number' &&
    typeof value.is_private === 'boolean'
  );
};

export const asEventCacheItem = (value: unknown): EventRead | null =>
  isEventCacheItem(value) ? value : null;
